import { useState } from 'react'

import {
  ActionIcon,
  Loader,
  Table,
  Tabs,
  Tooltip,
  Divider,
  Menu,
  Switch,
  Modal,
  Group,
  Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_ADMIN_COMPANY } from 'src/graphql/companies.graphql'
import {
  GET_COURSES,
  GET_TRAININGS,
  REFRESH_TRAININGS,
} from 'src/graphql/trainings.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconCopy from 'src/icons/IconCopy'

import TrainingSettingsCard from './components/TrainingSettingsCard'

const ITEMS_PER_PAGE = 10

const copy = (text: string) => {
  navigator.clipboard.writeText(text)
  toast('Copied to clipboard', 'success')
}

const CoursesTable = ({
  courses,
  loading,
  page,
  setPage,
  showProviderColumn,
}) => {
  const displayed = courses.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  )
  const colSpan = showProviderColumn ? 4 : 3
  return (
    <>
      <div className="h-full overflow-auto">
        <Table
          highlightOnHover
          classNames={{
            th: 'h-11 border-none bg-doubleNickel-gray-50 text-xs font-semibold text-doubleNickel-gray-700',
            td: 'text-xs text-doubleNickel-gray-500',
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="w-[5%]">ID</Table.Th>
              <Table.Th>Name</Table.Th>
              {showProviderColumn && <Table.Th>Provider</Table.Th>}
              <Table.Th>Lesson Count</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayed.map((course) => (
              <Table.Tr
                key={course.courseId}
                className="hover:bg-doubleNickel-gray-100"
              >
                <Table.Td>
                  <Tooltip label={course.courseId}>
                    <ActionIcon
                      variant="subtle"
                      size={14}
                      color="gray"
                      onClick={() => copy(course.courseId)}
                    >
                      <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{course.name}</Table.Td>
                {showProviderColumn && (
                  <Table.Td className="capitalize">{course.provider}</Table.Td>
                )}
                <Table.Td>
                  <Tooltip
                    label={
                      course.trainings.map((t) => t.training.name).join(', ') ||
                      ''
                    }
                  >
                    <div>{course.trainings.length}</div>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={colSpan}>
                  <div className="flex justify-center">
                    <Loader size="xs" color="gray" />
                  </div>
                </Table.Td>
              </Table.Tr>
            )}
            {!loading && courses.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={colSpan}>
                  <div className="flex justify-center text-xs text-doubleNickel-gray-700">
                    No courses found
                  </div>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </div>
      <PaginationArea
        page={page}
        totalPages={Math.ceil(courses.length / ITEMS_PER_PAGE)}
        onPageChange={setPage}
      />
    </>
  )
}

const LessonsTable = ({ lessons, loading, page, setPage }) => {
  const displayed = lessons.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  )
  return (
    <>
      <div className="h-full overflow-auto">
        <Table
          highlightOnHover
          classNames={{
            th: 'h-11 border-none bg-doubleNickel-gray-50 text-xs font-semibold text-doubleNickel-gray-700',
            td: 'text-xs text-doubleNickel-gray-500',
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="w-[5%]">ID</Table.Th>
              <Table.Th className="w-[5%]">Provider ID</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Language</Table.Th>
              <Table.Th>Duration</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayed.map((lesson) => (
              <Table.Tr
                key={lesson.trainingId}
                className="hover:bg-doubleNickel-gray-100"
              >
                <Table.Td>
                  <Tooltip label={lesson.trainingId}>
                    <ActionIcon
                      variant="subtle"
                      size={14}
                      color="gray"
                      onClick={() => copy(lesson.trainingId)}
                    >
                      <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Tooltip label={lesson.providerId || ''}>
                    <ActionIcon
                      variant="subtle"
                      size={14}
                      color="gray"
                      onClick={() =>
                        lesson.providerId && copy(lesson.providerId)
                      }
                    >
                      <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{lesson.name}</Table.Td>
                <Table.Td>{lesson.language || '-'}</Table.Td>
                <Table.Td>{lesson.duration || '-'}</Table.Td>
              </Table.Tr>
            ))}
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <div className="flex justify-center">
                    <Loader size="xs" color="gray" />
                  </div>
                </Table.Td>
              </Table.Tr>
            )}
            {!loading && lessons.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <div className="flex justify-center text-xs text-doubleNickel-gray-700">
                    No lessons found
                  </div>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </div>
      <PaginationArea
        page={page}
        totalPages={Math.ceil(lessons.length / ITEMS_PER_PAGE)}
        onPageChange={setPage}
      />
    </>
  )
}

const TrainingTab = () => {
  const { currentUser } = useAuth()
  const companyId = currentUser?.companyId

  const [subTab, setSubTab] = useState('courses')
  const [coursesPage, setCoursesPage] = useState(1)
  const [lessonsPage, setLessonsPage] = useState(1)

  const [guardEmptyCatalog, setGuardEmptyCatalog] = useState(true)
  const [refreshConfirmOpened, refreshConfirm] = useDisclosure(false)
  const [guardOffConfirmOpened, guardOffConfirm] = useDisclosure(false)

  const { data: coursesData, loading: coursesLoading } = useQuery(GET_COURSES)
  const { data: lessonsData, loading: lessonsLoading } = useQuery(GET_TRAININGS)

  const { data: companyData } = useQuery(GET_ADMIN_COMPANY, {
    variables: { id: companyId },
    skip: !companyId,
  })

  const company = companyData?.adminCompany

  const [refreshTrainingsMutation, { loading: refreshLoading }] = useMutation(
    REFRESH_TRAININGS,
    {
      refetchQueries: [
        {
          query: GET_COURSES,
        },
        {
          query: GET_TRAININGS,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: ({ refreshTrainings: result }) => {
        toast(
          `Trainings refreshed (${result.createdTrainings} lessons, ${result.createdCourses} courses)`,
          'success'
        )
      },
      onError: (error) => {
        toast(error.message, 'error')
      },
    }
  )

  const courses = coursesData?.courses?.items || []
  const lessons = lessonsData?.trainings?.items || []

  // Show provider column if company has both trainixCompanyId and lumaUsername
  const showProviderColumn =
    !!company?.trainixCompanyId && !!company?.lumaUsername

  return (
    <div className="flex h-full flex-col gap-6">
      <Modal
        opened={refreshConfirmOpened}
        onClose={refreshConfirm.close}
        title="Refresh trainings?"
        centered
      >
        <Text size="sm" className="text-doubleNickel-gray-700">
          Syncing pulls the latest Luma catalog. Any course no longer in Luma is
          archived, and drivers&apos; in-progress (not completed) assignments
          for it are permanently removed. Completed assignments are kept.
        </Text>
        {!guardEmptyCatalog && (
          <Text
            size="sm"
            className="mt-2 font-medium text-doubleNickel-error-500"
          >
            The empty-catalog guard is OFF: if Luma returns no badges, ALL Luma
            courses will be archived.
          </Text>
        )}
        <Group justify="flex-end" className="mt-4">
          <DNButton variant="secondary" onClick={refreshConfirm.close}>
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            onClick={() => {
              refreshConfirm.close()
              refreshTrainingsMutation({ variables: { guardEmptyCatalog } })
            }}
          >
            Refresh
          </DNButton>
        </Group>
      </Modal>

      <Modal
        opened={guardOffConfirmOpened}
        onClose={guardOffConfirm.close}
        title="Disable empty-catalog guard?"
        centered
      >
        <Text size="sm" className="text-doubleNickel-gray-700">
          With the guard off, a sync that returns an empty Luma catalog will
          archive every Luma course and permanently delete all their in-progress
          assignments. Leave it on unless you intentionally removed all badges.
        </Text>
        <Group justify="flex-end" className="mt-4">
          <DNButton variant="secondary" onClick={guardOffConfirm.close}>
            Keep guard on
          </DNButton>
          <DNButton
            variant="warning"
            onClick={() => {
              setGuardEmptyCatalog(false)
              guardOffConfirm.close()
            }}
          >
            Turn guard off
          </DNButton>
        </Group>
      </Modal>

      {company && (
        <TrainingSettingsCard
          companyData={{
            companyId: company.companyId,
            trainingEnabled: company.trainingEnabled ?? false,
            trainixCompanyId: company.trainixCompanyId,
            lumaUsername: company.lumaUsername,
            lumaPasswordSet: company.lumaPasswordSet ?? false,
          }}
          initialOpened={true}
        />
      )}
      <Tabs
        value={subTab}
        onChange={setSubTab}
        variant="pills"
        classNames={{
          tab: '[&[data-active]]:text-doubleNickel-brand-500 [&[data-active]]:bg-doubleNickel-brand-25 font-semibold text-sm text-doubleNickel-gray-500',
        }}
      >
        <Tabs.List className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Tabs.Tab value="courses">Courses</Tabs.Tab>
            <Tabs.Tab value="lessons">Lessons</Tabs.Tab>
          </div>
          <div className="flex items-center gap-3">
            <Tooltip
              multiline
              w={260}
              label="On (recommended): if a sync returns an empty Luma catalog, courses are NOT mass-archived. Turn off only if you intentionally removed all badges."
            >
              <div>
                <Switch
                  size="sm"
                  checked={guardEmptyCatalog}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setGuardEmptyCatalog(true)
                    } else {
                      guardOffConfirm.open()
                    }
                  }}
                  label="Guard empty catalog"
                  classNames={{
                    label: 'text-xs text-doubleNickel-gray-600',
                  }}
                />
              </div>
            </Tooltip>
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <DNButton variant="primary" className="text-xs">
                  Actions
                  <IconChevronDown className="ml-1 h-3.5 w-3.5 fill-none stroke-current" />
                </DNButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => refreshConfirm.open()}
                  disabled={refreshLoading}
                >
                  {refreshLoading ? 'Refreshing...' : 'Refresh Trainings'}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </Tabs.List>
        <Divider className="my-2" />
        <Tabs.Panel value="courses">
          <div className="flex h-full flex-col gap-4">
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-doubleNickel-gray-900">
                Courses
              </div>
              <div className="text-sm text-doubleNickel-gray-600">
                {courses.length} courses
              </div>
            </div>
            <CoursesTable
              courses={courses}
              loading={coursesLoading}
              page={coursesPage}
              setPage={setCoursesPage}
              showProviderColumn={showProviderColumn}
            />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="lessons">
          <div className="flex h-full flex-col gap-4">
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-doubleNickel-gray-900">
                Lessons
              </div>
              <div className="text-sm text-doubleNickel-gray-600">
                {lessons.length} lessons
              </div>
            </div>
            <LessonsTable
              lessons={lessons}
              loading={lessonsLoading}
              page={lessonsPage}
              setPage={setLessonsPage}
            />
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}

export default TrainingTab
